import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  Chip,
  Grid,
  Paper,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import InfoIcon from "@material-ui/icons/Info";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import { useFormik } from "formik";
import * as Yup from "yup";
import { makeStyles } from "@material-ui/styles";
import { useGetAbonosFacturaQuery } from "src/services/facturasApi";
import { capitalizarPrimeraLetra } from "src/utils/sharedFunctions";

// Estados que no se derivan de los pagos: el backend no los pisa al recalcular
// (`estadoFacturaSegunPagos.js`), asi que para ellos no hay estado resultante que
// anticipar.
const ESTADOS_NO_RECALCULABLES = ["INCOBRABLE", "ANULADO", "CORRECCIÓN"];

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      minWidth: "550px",
      maxWidth: "650px",
    },
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "& .MuiTypography-root": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
  },
  dialogContent: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2),
  },
  infoBox: {
    backgroundColor: theme.palette.info.light,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  warningBox: {
    backgroundColor: theme.palette.warning.light,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  radioOption: {
    border: "1px solid #e0e0e0",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedRadio: {
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.selected,
  },
  calculoBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  montoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 0),
  },
  montoNuevo: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.success.light,
    borderRadius: theme.shape.borderRadius,
    fontWeight: 600,
  },
  resumenBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  actionsContainer: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
}));

/**
 * Modal de corrección de montos con wizard de 3 pasos
 * PASO 1: Seleccionar tipo de corrección (AUMENTO o DISMINUCION)
 * PASO 2: Ingresar montoDelta y ver cálculo en tiempo real
 * PASO 3: Ingresar motivo y confirmar
 */
export default function ModalCorregirMonto({
  open,
  onClose,
  factura,
  onCorregir,
  isLoading = false,
}) {
  const classes = useStyles();
  const [paso, setPaso] = useState(1);

  // Validación para cada paso
  const validationSchema = Yup.object().shape({
    tipoCorreccion: Yup.string()
      .oneOf(["AUMENTO", "DISMINUCION"], "Tipo de corrección inválido")
      .required("Debe seleccionar un tipo de corrección"),
    montoDelta: Yup.number()
      .positive("El monto debe ser mayor a 0")
      .integer("El monto debe ser un número entero")
      .required("El monto es obligatorio")
      .test(
        "no-mayor-vigente",
        "No se puede reducir más del monto vigente",
        function (value) {
          const { tipoCorreccion } = this.parent;
          if (tipoCorreccion === "DISMINUCION" && value > montoVigente) {
            return false;
          }
          return true;
        },
      ),
    motivoCorreccion: Yup.string()
      .min(10, "El motivo debe tener al menos 10 caracteres")
      .max(500, "El motivo no puede exceder 500 caracteres")
      .required("El motivo de corrección es obligatorio"),
  });

  const formik = useFormik({
    initialValues: {
      tipoCorreccion: "",
      montoDelta: "",
      motivoCorreccion: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      await onCorregir(values);
      handleClose();
    },
  });

  const { values, errors, touched, handleChange, handleSubmit, resetForm } =
    formik;

  const handleClose = () => {
    resetForm();
    setPaso(1);
    onClose();
  };

  const handleSiguiente = () => {
    setPaso(paso + 1);
  };

  const handleVolver = () => {
    setPaso(paso - 1);
  };

  // Obtener monto vigente y calcular nuevo monto
  const montoVigente = factura?.valorPesos || factura?.total || 0;
  const montoDelta = Number(values.montoDelta) || 0;
  const montoNuevo =
    values.tipoCorreccion === "AUMENTO"
      ? montoVigente + montoDelta
      : montoVigente - montoDelta;

  // Lo ya abonado sale de su propio endpoint: el listado de facturas no trae
  // `totalAbonado` (solo lo devuelve el portal del cliente), así que leerlo de la fila daba
  // 0 siempre y el tope de la disminución no bloqueaba nada. Mientras la consulta viaja no
  // se deja avanzar, para no validar contra un total que todavía no se conoce.
  //
  // Si la consulta falla se trata igual que mientras carga, porque `totalAbonado` caeria a
  // 0 y el tope volveria a no bloquear nada. El servidor revalida antes de emitir, asi que
  // no se llegaria a generar un documento malo; lo que se evita es que el usuario reciba un
  // error generico al final del flujo en vez del aviso en este paso, que es justo para lo
  // que existe esta consulta.
  const tipoFactura = factura?.tipo || "PLAN";
  const {
    data: datosAbonos,
    isFetching: cargandoAbonos,
    isError: errorAbonos,
  } = useGetAbonosFacturaQuery(
    { id: factura?.id, tipo: tipoFactura },
    { skip: !open || !factura?.id, refetchOnMountOrArgChange: true },
  );
  const totalAbonado = datosAbonos?.totalAbonado || 0;

  // Estado que le quedaria a la factura despues de la correccion. Replica la regla
  // del backend, que la deriva de los pagos vigentes contra el monto nuevo y no del
  // estado anterior: pagado >= total -> PAGADO, pagado > 0 -> ABONO, sin pagos ->
  // FACTURADO. Es solo informativo; quien decide sigue siendo el backend.
  const estadoActual = factura?.estadoFactura;
  const estadoResultante = ESTADOS_NO_RECALCULABLES.includes(estadoActual)
    ? null
    : totalAbonado > 0 && totalAbonado >= montoNuevo
      ? "PAGADO"
      : totalAbonado > 0
        ? "ABONO"
        : "FACTURADO";

  // Validar en tiempo real para PASO 2
  const validarPaso2 = () => {
    if (cargandoAbonos || errorAbonos) return false;
    if (!montoDelta || montoDelta <= 0) return false;

    if (values.tipoCorreccion === "DISMINUCION") {
      // Validar que no sea mayor al vigente
      if (montoDelta > montoVigente) return false;
      // Validar que el nuevo monto no sea menor a los abonos
      if (montoNuevo < totalAbonado) return false;
      // Validar que el nuevo monto no sea 0 o negativo
      if (montoNuevo <= 0) return false;
    }

    return true;
  };

  // Formatear montos
  const formatMonto = (monto) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
    }).format(monto);
  };

  // Reset al abrir/cerrar
  useEffect(() => {
    if (!open) {
      resetForm();
      setPaso(1);
    }
  }, [open, resetForm]);

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : handleClose}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={classes.dialogTitle}>
        <AttachMoneyIcon />
        {paso === 1 && "Corregir Monto de Factura"}
        {paso === 2 &&
          `${
            values.tipoCorreccion === "AUMENTO" ? "Aumentar" : "Disminuir"
          } Monto`}
        {paso === 3 && "Confirmar Corrección"}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent className={classes.dialogContent}>
          {/* PASO 1: Seleccionar tipo de corrección */}
          {paso === 1 && (
            <>
              <Box className={classes.infoBox}>
                <InfoIcon color="inherit" />
                <Box>
                  <Typography variant="body2">
                    <strong>Información de la factura:</strong>
                  </Typography>
                  <Typography variant="body2">
                    Folio: <strong>{factura?.folio}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Monto vigente: <strong>{formatMonto(montoVigente)}</strong>
                  </Typography>
                  {cargandoAbonos ? (
                    <Typography variant="body2" color="textSecondary">
                      Consultando pagos registrados…
                    </Typography>
                  ) : errorAbonos ? (
                    <Typography variant="body2" color="error">
                      No se pudieron consultar los pagos registrados.
                    </Typography>
                  ) : (
                    totalAbonado > 0 && (
                      <Typography variant="body2" color="textSecondary">
                        Total abonado: {formatMonto(totalAbonado)}
                      </Typography>
                    )
                  )}
                </Box>
              </Box>

              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" style={{ marginBottom: 16 }}>
                  ¿Qué tipo de corrección desea realizar?
                </FormLabel>
                <RadioGroup
                  name="tipoCorreccion"
                  value={values.tipoCorreccion}
                  onChange={handleChange}
                >
                  <Paper
                    className={`${classes.radioOption} ${
                      values.tipoCorreccion === "DISMINUCION"
                        ? classes.selectedRadio
                        : ""
                    }`}
                  >
                    <FormControlLabel
                      value="DISMINUCION"
                      control={<Radio color="primary" />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <TrendingDownIcon color="error" />
                          <Box>
                            <Typography variant="subtitle2">
                              Disminuir monto
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Genera una Nota de Crédito (NC)
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </Paper>

                  <Paper
                    className={`${classes.radioOption} ${
                      values.tipoCorreccion === "AUMENTO"
                        ? classes.selectedRadio
                        : ""
                    }`}
                  >
                    <FormControlLabel
                      value="AUMENTO"
                      control={<Radio color="primary" />}
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <TrendingUpIcon color="primary" />
                          <Box>
                            <Typography variant="subtitle2">
                              Aumentar monto
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Genera una Nota de Débito (ND)
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </Paper>
                </RadioGroup>
                {errors.tipoCorreccion && touched.tipoCorreccion && (
                  <Typography variant="caption" color="error">
                    {errors.tipoCorreccion}
                  </Typography>
                )}
              </FormControl>
            </>
          )}

          {/* PASO 2: Ingresar monto */}
          {paso === 2 && (
            <>
              <Box className={classes.infoBox}>
                <InfoIcon color="inherit" />
                <Typography variant="body2">
                  Ingrese el monto a{" "}
                  {values.tipoCorreccion === "AUMENTO" ? "aumentar" : "reducir"}
                  . El sistema calculará automáticamente el nuevo monto total.
                </Typography>
              </Box>

              {/* Sin el total abonado no hay contra que validar la disminucion, asi que se
                  dice por que no se puede continuar en vez de dejar avanzar a ciegas. */}
              {errorAbonos && (
                <Alert severity="error" style={{ marginTop: 16 }}>
                  No se pudieron consultar los pagos registrados de esta
                  factura, así que no es posible validar el monto. Cierre el
                  modal y vuelva a intentarlo.
                </Alert>
              )}

              <TextField
                fullWidth
                name="montoDelta"
                label={`Monto a ${
                  values.tipoCorreccion === "AUMENTO" ? "aumentar" : "reducir"
                }`}
                value={values.montoDelta}
                onChange={handleChange}
                type="number"
                inputProps={{
                  min: 1,
                  step: 1,
                  onWheel: (e) => e.target.blur(),
                }}
                placeholder="Ejemplo: 30000"
                error={touched.montoDelta && Boolean(errors.montoDelta)}
                helperText={touched.montoDelta && errors.montoDelta}
                autoFocus
              />

              {/* Cálculo en tiempo real */}
              {montoDelta > 0 && (
                <Box className={classes.calculoBox}>
                  <Typography variant="subtitle2" gutterBottom>
                    Cálculo de corrección:
                  </Typography>
                  <Divider style={{ margin: "8px 0" }} />

                  <Box className={classes.montoRow}>
                    <Typography variant="body2">Monto vigente:</Typography>
                    <Typography variant="body2">
                      {formatMonto(montoVigente)}
                    </Typography>
                  </Box>

                  <Box className={classes.montoRow}>
                    <Typography variant="body2">
                      {values.tipoCorreccion === "AUMENTO"
                        ? "Aumentar:"
                        : "Reducir:"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        values.tipoCorreccion === "AUMENTO"
                          ? "primary"
                          : "error"
                      }
                    >
                      {values.tipoCorreccion === "AUMENTO" ? "+" : "-"}
                      {formatMonto(montoDelta)}
                    </Typography>
                  </Box>

                  {totalAbonado > 0 &&
                    values.tipoCorreccion === "DISMINUCION" && (
                      <Box className={classes.montoRow}>
                        <Typography variant="body2" color="textSecondary">
                          Total abonado:
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {formatMonto(totalAbonado)}
                        </Typography>
                      </Box>
                    )}

                  <Divider style={{ margin: "8px 0" }} />

                  <Box className={classes.montoRow}>
                    <Typography variant="subtitle1">
                      <strong>Monto nuevo:</strong>
                    </Typography>
                    <Typography variant="h6" color="primary">
                      <strong>{formatMonto(montoNuevo)}</strong>
                    </Typography>
                  </Box>

                  {estadoResultante && validarPaso2() && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      {estadoResultante === estadoActual
                        ? `La factura se mantendría en ${capitalizarPrimeraLetra(
                            estadoResultante,
                          )}.`
                        : estadoActual
                          ? `Con este monto la factura pasaría de ${capitalizarPrimeraLetra(
                              estadoActual,
                            )} a ${capitalizarPrimeraLetra(estadoResultante)}.`
                          : `Con este monto la factura quedaría en ${capitalizarPrimeraLetra(
                              estadoResultante,
                            )}.`}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Alertas de validación */}
              {montoDelta > 0 && values.tipoCorreccion === "DISMINUCION" && (
                <>
                  {montoDelta > montoVigente && (
                    <Alert severity="error" style={{ marginTop: 16 }}>
                      No se puede reducir ${formatMonto(montoDelta)} de un monto
                      vigente de {formatMonto(montoVigente)}
                    </Alert>
                  )}
                  {montoNuevo < totalAbonado && (
                    <Alert severity="error" style={{ marginTop: 16 }}>
                      No se puede reducir a {formatMonto(montoNuevo)} porque ya
                      se han abonado {formatMonto(totalAbonado)}
                    </Alert>
                  )}
                  {montoNuevo <= 0 && (
                    <Alert severity="warning" style={{ marginTop: 16 }}>
                      El nuevo monto sería $0 o negativo. Use la función de
                      anulación en su lugar.
                    </Alert>
                  )}
                </>
              )}
            </>
          )}

          {/* PASO 3: Confirmar */}
          {paso === 3 && (
            <>
              <Box className={classes.warningBox}>
                <InfoIcon color="inherit" />
                <Typography variant="body2">
                  <strong>Atención:</strong> Esta acción generará un{" "}
                  {values.tipoCorreccion === "AUMENTO"
                    ? "Nota de Débito (ND)"
                    : "Nota de Crédito (NC)"}{" "}
                  y modificará el monto de la factura. Esta acción no se puede
                  deshacer.
                </Typography>
              </Box>

              <Box className={classes.resumenBox}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Resumen de cambios:</strong>
                </Typography>
                <Divider style={{ margin: "8px 0 16px 0" }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Factura
                    </Typography>
                    <Typography variant="body2">
                      Folio {factura?.folio}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Tipo de corrección
                    </Typography>
                    <Typography variant="body2">
                      <Chip
                        size="small"
                        label={values.tipoCorreccion}
                        color={
                          values.tipoCorreccion === "AUMENTO"
                            ? "primary"
                            : "default"
                        }
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      Monto vigente
                    </Typography>
                    <Typography variant="body2">
                      {formatMonto(montoVigente)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      {values.tipoCorreccion === "AUMENTO"
                        ? "Aumentar"
                        : "Reducir"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        values.tipoCorreccion === "AUMENTO"
                          ? "primary"
                          : "error"
                      }
                    >
                      {values.tipoCorreccion === "AUMENTO" ? "+" : "-"}
                      {formatMonto(montoDelta)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Monto nuevo
                    </Typography>
                    <Typography variant="h6" color="primary">
                      <strong>{formatMonto(montoNuevo)}</strong>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <TextField
                fullWidth
                name="motivoCorreccion"
                label="Motivo de la corrección *"
                value={values.motivoCorreccion}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder="Ejemplo: Descuento por pronto pago acordado con el cliente"
                error={
                  touched.motivoCorreccion && Boolean(errors.motivoCorreccion)
                }
                helperText={
                  touched.motivoCorreccion
                    ? errors.motivoCorreccion
                    : "Mínimo 10 caracteres, máximo 500"
                }
              />
            </>
          )}
        </DialogContent>

        <DialogActions className={classes.actionsContainer}>
          {/* Botones PASO 1 */}
          {paso === 1 && (
            <>
              <Button onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSiguiente}
                disabled={!values.tipoCorreccion || isLoading}
              >
                Continuar
              </Button>
            </>
          )}

          {/* Botones PASO 2 */}
          {paso === 2 && (
            <>
              <Button onClick={handleVolver} disabled={isLoading}>
                Volver
              </Button>
              <Button onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSiguiente}
                disabled={!validarPaso2() || isLoading}
              >
                Continuar
              </Button>
            </>
          )}

          {/* Botones PASO 3 */}
          {paso === 3 && (
            <>
              <Button onClick={handleVolver} disabled={isLoading}>
                Volver
              </Button>
              <Button onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                color="secondary"
                loading={isLoading}
                disabled={
                  !values.motivoCorreccion ||
                  values.motivoCorreccion.length < 10 ||
                  isLoading
                }
              >
                Confirmar Corrección
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
